import React, { useState, useEffect, useRef } from 'react';

const DELIVERY_STATUS_OPTIONS = [
  { value: 'payment_successful', label: 'Received by Merchant' },
  { value: 'claimed', label: 'Preparing' },
  { value: 'ready_for_delivery', label: 'Ready for Delivery' },
  { value: 'out_for_delivery', label: 'Delivering' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const CC_STATUS_OPTIONS = [
  { value: 'payment_successful', label: 'Received by Merchant' },
  { value: 'cc_claimed', label: 'Preparing' },
  { value: 'cc_ready_for_pickup', label: 'Ready for Pickup' },
  { value: 'cc_completed', label: 'Collected' },
  { value: 'cc_cancelled', label: 'Cancelled' },
];

interface StatusChangeDialogProps {
  open: boolean;
  orderId: string;
  isClickAndCollect: boolean;
  existingDetails?: string;
  loading?: boolean;
  onConfirm: (status: string, note: string) => void;
  onCancel: () => void;
}

export const StatusChangeDialog: React.FC<StatusChangeDialogProps> = ({
  open,
  orderId,
  isClickAndCollect,
  existingDetails,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const options = isClickAndCollect ? CC_STATUS_OPTIONS : DELIVERY_STATUS_OPTIONS;
  const [selectedStatus, setSelectedStatus] = useState(options[0].value);
  const [noteText, setNoteText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      const opts = isClickAndCollect ? CC_STATUS_OPTIONS : DELIVERY_STATUS_OPTIONS;
      setSelectedStatus(opts[0].value);
      setNoteText('');
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
    }
  }, [open, isClickAndCollect]);

  if (!open) return null;

  const canConfirm = selectedStatus && noteText.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
        <div className="px-6 py-4 space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Change Status — {orderId}</h3>

          {/* Status dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              disabled={loading}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            >
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Existing additional details (read-only) */}
          {existingDetails && existingDetails.trim() && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Existing Details</label>
              <div className="w-full border border-gray-200 bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap max-h-32 overflow-y-auto">
                {existingDetails}
              </div>
            </div>
          )}

          {/* Support note textarea */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Support Note</label>
            <textarea
              ref={textareaRef}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Describe the reason for changing the status..."
              disabled={loading}
              rows={4}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            />
            <p className="mt-1 text-xs text-red-600 font-medium">Please note that this message will be visible to the customer!</p>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onConfirm(selectedStatus, noteText.trim())}
              disabled={loading || !canConfirm}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-alps-primary hover:bg-alps-primary-dark disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
